import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { registerPasskey, listPasskeys, removePasskey } from '../utils/passkeyUtils'

const CustomerPortal = () => {
  const { user } = useAuth()
  const { isNight } = useTheme()
  const [activeView, setActiveView] = useState('dashboard')
  const [passkeys, setPasskeys] = useState([])
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [passkeyMessage, setPasskeyMessage] = useState(null)

  const servicePricing = [
    {
      name: 'External Network Penetration Test',
      priceRange: '$6,000 - $25,000+',
      basis: 'Based on IP count',
      duration: '5-7 business days',
      description: 'Comprehensive assessment of internet-facing infrastructure'
    },
    {
      name: 'External Web Application Penetration Test',
      priceRange: '$4,000 - $50,000+',
      basis: 'Based on application complexity',
      duration: '7-14 business days',
      description: 'In-depth security testing of web applications and APIs'
    },
    {
      name: 'External OSINT Assessment',
      priceRange: '$1,500 - $8,000',
      basis: 'Based on assessment depth',
      duration: '3-5 business days',
      description: 'Open Source Intelligence gathering and analysis'
    },
    {
      name: 'Internal Network Security Controls Assessment',
      priceRange: '$10,000 - $75,000+',
      basis: 'Based on network size and complexity',
      duration: '10-15 business days',
      description: 'Comprehensive internal network security evaluation'
    },
    {
      name: 'Internal Vulnerability Assessment',
      priceRange: '$2,000 - $35,000',
      basis: 'Based on IP count',
      duration: 'Variable, recommended quarterly',
      description: 'Systematic vulnerability scanning and analysis'
    }
  ]

  const packages = [
    {
      name: 'External Security Package',
      services: ['External Network Penetration Test', 'External Web Application Penetration Test', 'External OSINT Assessment'],
      discount: '10%',
      description: 'Complete external security assessment bundle'
    },
    {
      name: 'Internal Security Package',
      services: ['Internal Network Security Controls Assessment', 'Internal Vulnerability Assessment'],
      discount: '10%',
      description: 'Comprehensive internal security assessment bundle'
    },
    {
      name: 'Complete Security Assessment',
      services: ['All 5 services'],
      discount: '15%',
      description: 'Full spectrum security assessment (all services included)'
    },
    {
      name: 'Annual Security Program',
      services: ['Ongoing retainer with quarterly assessments'],
      discount: '20%',
      description: 'Year-round security partnership with regular assessments'
    }
  ]

  const serviceDeliverables = [
    {
      name: 'External Network Penetration Test',
      methodology: [
        'Reconnaissance and information gathering',
        'Port scanning and service enumeration',
        'Vulnerability identification and analysis',
        'Exploitation attempts on identified vulnerabilities',
        'Post-exploitation analysis and privilege escalation testing'
      ],
      deliverables: [
        'Executive summary with risk overview',
        'Detailed technical findings report',
        'Vulnerability classification (Critical, High, Medium, Low)',
        'Step-by-step exploitation evidence',
        'Remediation recommendations with prioritization',
        'Retest report after fixes (optional add-on)'
      ]
    },
    {
      name: 'External Web Application Penetration Test',
      methodology: [
        'Application mapping and reconnaissance',
        'Authentication and session management testing',
        'Input validation and injection testing (SQL, XSS, etc.)',
        'Business logic and authorization testing',
        'API security assessment',
        'OWASP Top 10 vulnerability testing'
      ],
      deliverables: [
        'Executive summary for stakeholders',
        'Detailed vulnerability report with evidence',
        'OWASP Top 10 compliance assessment',
        'Proof-of-concept exploits and screenshots',
        'Secure coding recommendations',
        'Remediation guidance and best practices'
      ]
    },
    {
      name: 'External OSINT Assessment',
      methodology: [
        'Public records and domain information gathering',
        'Social media footprint analysis',
        'Exposed credentials and data leak detection',
        'Third-party service enumeration',
        'Employee information gathering',
        'Brand reputation and impersonation risk analysis'
      ],
      deliverables: [
        'Comprehensive OSINT report',
        'Exposed credential listing',
        'Data leak analysis',
        'Social engineering risk assessment',
        'Digital footprint summary',
        'Security awareness recommendations'
      ]
    },
    {
      name: 'Internal Network Security Controls Assessment',
      methodology: [
        'Internal network architecture review',
        'Active Directory security assessment',
        'Privilege escalation testing',
        'Lateral movement analysis',
        'Network segmentation effectiveness testing',
        'Critical asset access control review'
      ],
      deliverables: [
        'Executive summary with security posture overview',
        'Detailed technical assessment report',
        'Active Directory security findings',
        'Network segmentation analysis',
        'Privilege escalation paths identified',
        'Remediation roadmap with priorities'
      ]
    },
    {
      name: 'Internal Vulnerability Assessment',
      methodology: [
        'Comprehensive network scanning',
        'Asset discovery and inventory',
        'Vulnerability identification and classification',
        'Patch level analysis',
        'Configuration review',
        'Risk-based prioritization'
      ],
      deliverables: [
        'Executive dashboard with metrics',
        'Detailed vulnerability report',
        'Asset inventory and classification',
        'Risk-prioritized remediation plan',
        'Patch management recommendations',
        'Trend analysis and comparison reports (for recurring assessments)'
      ]
    }
  ]

  // Load passkeys when security view is active
  useEffect(() => {
    if (activeView === 'security') {
      loadPasskeys()
    }
  }, [activeView])

  const loadPasskeys = async () => {
    const token = localStorage.getItem('token')
    if (!token) return

    const result = await listPasskeys(token)
    if (result.success) {
      setPasskeys(result.passkeys)
    }
  }

  const handleRegisterPasskey = async () => {
    setPasskeyLoading(true)
    setPasskeyMessage(null)

    const token = localStorage.getItem('token')
    if (!token) {
      setPasskeyMessage({ type: 'error', text: 'Not authenticated' })
      setPasskeyLoading(false)
      return
    }

    const result = await registerPasskey(token)

    if (result.success) {
      setPasskeyMessage({ type: 'success', text: result.message })
      await loadPasskeys() // Refresh list
    } else {
      setPasskeyMessage({ type: 'error', text: result.message })
    }

    setPasskeyLoading(false)
  }

  const handleRemovePasskey = async (passkeyId) => {
    if (!confirm('Are you sure you want to remove this passkey?')) {
      return
    }

    const token = localStorage.getItem('token')
    const result = await removePasskey(passkeyId, token)

    if (result.success) {
      setPasskeyMessage({ type: 'success', text: result.message })
      await loadPasskeys() // Refresh list
    } else {
      setPasskeyMessage({ type: 'error', text: result.message })
    }
  }

  return (
    <div className={`min-h-screen py-12 ${isNight ? 'bg-void' : 'bg-gradient-to-b from-gray-50 to-gray-100'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
            {isNight ? `ACCESS GRANTED: ${user?.firstName}` : `Welcome, ${user?.firstName}!`}
          </h1>
          <p className={`mt-2 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
            {user?.company} - {isNight ? 'Secure customer portal' : 'Your personalized customer portal'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
              <h2 className={`text-xl font-semibold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                {isNight ? 'PROTOCOLS' : 'Quick Actions'}
              </h2>
              <div className="space-y-3">
                <button
                  onClick={() => setActiveView('dashboard')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm ${
                    activeView === 'dashboard'
                      ? isNight ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/50' : 'bg-primary-600 text-white'
                      : isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  🏠 {isNight ? 'DASHBOARD' : 'Dashboard'}
                </button>
                <button
                  onClick={() => setActiveView('reports')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm ${
                    activeView === 'reports'
                      ? isNight ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/50' : 'bg-primary-600 text-white'
                      : isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  📄 {isNight ? 'VIEW REPORTS' : 'View Reports'}
                </button>
                <button
                  onClick={() => setActiveView('proposals')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm ${
                    activeView === 'proposals'
                      ? isNight ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/50' : 'bg-primary-600 text-white'
                      : isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  📊 {isNight ? 'VIEW PROPOSALS' : 'View Proposals'}
                </button>
                <button
                  onClick={() => setActiveView('pricing')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm ${
                    activeView === 'pricing'
                      ? isNight ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/50' : 'bg-primary-600 text-white'
                      : isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  💰 {isNight ? 'PRICING' : 'View Services Pricing'}
                </button>
                <button
                  onClick={() => setActiveView('deliverables')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm ${
                    activeView === 'deliverables'
                      ? isNight ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/50' : 'bg-primary-600 text-white'
                      : isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  📋 {isNight ? 'DELIVERABLES' : 'View Service Deliverables'}
                </button>
                <button
                  onClick={() => setActiveView('security')}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm ${
                    activeView === 'security'
                      ? isNight ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/50' : 'bg-primary-600 text-white'
                      : isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  🔐 {isNight ? 'SECURITY' : 'Security Settings'}
                </button>
                <a
                  href="/book-appointment"
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm block ${
                    isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  📅 {isNight ? 'SCHEDULE' : 'Schedule Meeting'}
                </a>
                <a
                  href="mailto:info@vigilantedge.tech"
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all backdrop-blur-sm block ${
                    isNight ? 'bg-void-elevated/50 text-gray-300 border border-cyber-cyan/10 hover:border-cyber-cyan/30 hover:text-cyber-cyan' : 'bg-white/50 text-primary-700 hover:bg-white/70 border border-white/50'
                  }`}
                >
                  💬 {isNight ? 'SUPPORT' : 'Contact Support'}
                </a>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dashboard View */}
            {activeView === 'dashboard' && (
              <>
                <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                  <h2 className={`text-xl font-semibold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                    {isNight ? 'RECENT REPORTS' : 'Recent Reports'}
                  </h2>
                  <div className={isNight ? 'text-gray-400' : 'text-gray-600'}>
                    {isNight ? 'No data streams available. Reports will be uploaded to this terminal.' : 'No reports available yet. Your security assessment reports will appear here.'}
                  </div>
                </div>

                <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                  <h2 className={`text-xl font-semibold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                    {isNight ? 'ACTIVE PROPOSALS' : 'Active Proposals'}
                  </h2>
                  <div className={isNight ? 'text-gray-400' : 'text-gray-600'}>
                    {isNight ? 'No pending proposals. Awaiting new transmissions.' : 'No active proposals. We\'ll notify you when new proposals are available.'}
                  </div>
                </div>

                <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                  <h2 className={`text-xl font-semibold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                    {isNight ? 'ACTIVITY LOG' : 'Recent Activity'}
                  </h2>
                  <div className="space-y-3">
                    <div className={`flex items-center text-sm ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span className={`mr-2 ${isNight ? 'text-cyber-cyan' : 'text-green-500'}`}>●</span>
                      {isNight ? 'Access initialized' : 'Account created'} - {new Date().toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Reports View */}
            {activeView === 'reports' && (
              <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                <h2 className={`text-xl font-semibold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                  {isNight ? 'SECURITY ASSESSMENT LOGS' : 'Security Assessment Reports'}
                </h2>
                <div className={isNight ? 'text-gray-400' : 'text-gray-600'}>
                  {isNight ? 'No assessment data available. Completed scans will be archived here.' : 'No reports available yet. Your security assessment reports will appear here once assessments are completed.'}
                </div>
              </div>
            )}

            {/* Proposals View */}
            {activeView === 'proposals' && (
              <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                <h2 className={`text-xl font-semibold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                  {isNight ? 'SERVICE PROPOSALS' : 'Service Proposals'}
                </h2>
                <div className={isNight ? 'text-gray-400' : 'text-gray-600'}>
                  {isNight ? 'No pending proposals. New transmissions will appear here.' : 'No active proposals. We\'ll notify you when new proposals are available for review.'}
                </div>
              </div>
            )}

            {/* Pricing View */}
            {activeView === 'pricing' && (
              <>
                <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                  <h2 className={`text-2xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                    {isNight ? 'SERVICE PRICING' : 'Service Pricing'}
                  </h2>
                  <p className={`mb-6 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isNight ? 'Current rate structure for security protocols. Contact for custom configurations.' : 'Below is our pricing structure for individual security services. Contact us for a detailed quote based on your specific requirements.'}
                  </p>
                  <div className="space-y-4">
                    {servicePricing.map((service, index) => (
                      <div key={index} className={`rounded-lg p-5 transition-all backdrop-blur-sm ${
                        isNight
                          ? 'bg-void-elevated/50 border border-cyber-cyan/20 hover:border-cyber-cyan/40'
                          : 'bg-white/50 border border-gray-200/50 hover:shadow-md hover:bg-white/70'
                      }`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold ${isNight ? 'text-white' : 'text-gray-900'}`}>{service.name}</h3>
                            <p className={`text-sm mt-1 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>{service.description}</p>
                          </div>
                          <div className="text-right ml-4">
                            <div className={`text-lg font-bold ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>{service.priceRange}</div>
                            <div className={`text-xs ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>{service.basis}</div>
                          </div>
                        </div>
                        <div className={`flex items-center text-sm ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>
                          <span className="mr-4">⏱️ {service.duration}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                  <h2 className={`text-2xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                    {isNight ? 'SERVICE PACKAGES' : 'Service Packages'}
                  </h2>
                  <p className={`mb-6 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isNight ? 'Bundled protocols with enhanced savings. All packages include full documentation.' : 'Save on bundled services with our package offerings. All packages include comprehensive reporting and remediation guidance.'}
                  </p>
                  <div className="space-y-4">
                    {packages.map((pkg, index) => (
                      <div key={index} className={`rounded-lg p-5 backdrop-blur-sm ${
                        isNight
                          ? 'bg-cyber-magenta/10 border border-cyber-magenta/30'
                          : 'bg-primary-50/70 border border-primary-200/50'
                      }`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className={`text-lg font-semibold ${isNight ? 'text-white' : 'text-gray-900'}`}>{pkg.name}</h3>
                              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded ${
                                isNight ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/30' : 'bg-green-100 text-green-800'
                              }`}>
                                Save {pkg.discount}
                              </span>
                            </div>
                            <p className={`text-sm mt-2 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>{pkg.description}</p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <p className={`text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>Includes:</p>
                          <ul className={`text-sm space-y-1 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                            {pkg.services.map((service, idx) => (
                              <li key={idx} className="flex items-center">
                                <span className={`mr-2 ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>✓</span>
                                {service}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Deliverables View */}
            {activeView === 'deliverables' && (
              <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                <h2 className={`text-2xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                  {isNight ? 'METHODOLOGY & DELIVERABLES' : 'Service Deliverables & Methodology'}
                </h2>
                <p className={`mb-8 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isNight ? 'Detailed breakdown of assessment protocols and output documentation.' : 'Detailed breakdown of our assessment methodology and deliverables for each service.'}
                </p>
                <div className="space-y-8">
                  {serviceDeliverables.map((service, index) => (
                    <div key={index} className={`pb-8 last:border-b-0 ${isNight ? 'border-b border-cyber-cyan/20' : 'border-b border-gray-200'}`}>
                      <h3 className={`text-xl font-semibold mb-4 ${isNight ? 'text-white' : 'text-gray-900'}`}>{service.name}</h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className={`text-sm font-semibold uppercase mb-3 ${isNight ? 'text-cyber-magenta' : 'text-primary-600'}`}>
                            {isNight ? 'METHODOLOGY' : 'Methodology'}
                          </h4>
                          <ul className="space-y-2">
                            {service.methodology.map((item, idx) => (
                              <li key={idx} className={`flex items-start text-sm ${isNight ? 'text-gray-400' : 'text-gray-700'}`}>
                                <span className={`mr-2 mt-1 ${isNight ? 'text-cyber-magenta' : 'text-primary-600'}`}>▸</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className={`text-sm font-semibold uppercase mb-3 ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                            {isNight ? 'DELIVERABLES' : 'Deliverables'}
                          </h4>
                          <ul className="space-y-2">
                            {service.deliverables.map((item, idx) => (
                              <li key={idx} className={`flex items-start text-sm ${isNight ? 'text-gray-400' : 'text-gray-700'}`}>
                                <span className={`mr-2 mt-1 ${isNight ? 'text-cyber-cyan' : 'text-green-600'}`}>✓</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Security Settings View */}
            {activeView === 'security' && (
              <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
                <h2 className={`text-2xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                  {isNight ? 'SECURITY PROTOCOLS' : 'Security Settings'}
                </h2>
                <p className={`mb-8 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isNight ? 'Configure biometric authentication for enhanced access security.' : 'Manage your account security with passkeys - a passwordless authentication method that\'s more secure than traditional passwords.'}
                </p>

                {/* Passkey Message */}
                {passkeyMessage && (
                  <div
                    className={`mb-6 p-4 rounded-lg backdrop-blur-sm ${
                      passkeyMessage.type === 'success'
                        ? isNight ? 'bg-green-900/40 text-green-400 border border-green-500/30' : 'bg-green-50/80 text-green-800 border border-green-200/50'
                        : isNight ? 'bg-red-900/40 text-red-400 border border-red-500/30' : 'bg-red-50/80 text-red-800 border border-red-200/50'
                    }`}
                  >
                    {passkeyMessage.text}
                  </div>
                )}

                {/* Register New Passkey */}
                <div className={`mb-8 pb-8 ${isNight ? 'border-b border-cyber-cyan/20' : 'border-b border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${isNight ? 'text-white' : 'text-gray-900'}`}>
                    {isNight ? 'REGISTER NEW PASSKEY' : 'Register New Passkey'}
                  </h3>
                  <p className={`text-sm mb-4 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isNight ? 'Enable biometric authentication (fingerprint, face recognition) or device PIN.' : 'Passkeys allow you to sign in using your device\'s biometric authentication (fingerprint, face recognition) or PIN.'}
                  </p>
                  <button
                    onClick={handleRegisterPasskey}
                    disabled={passkeyLoading}
                    className={`disabled:opacity-50 disabled:cursor-not-allowed ${isNight ? 'btn-neon' : 'btn-primary'}`}
                  >
                    {passkeyLoading ? (isNight ? 'REGISTERING...' : 'Registering...') : (isNight ? '+ ADD PASSKEY' : '+ Add Passkey')}
                  </button>
                </div>

                {/* Registered Passkeys List */}
                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${isNight ? 'text-white' : 'text-gray-900'}`}>
                    {isNight ? `YOUR PASSKEYS (${passkeys.length})` : `Your Passkeys (${passkeys.length})`}
                  </h3>
                  {passkeys.length === 0 ? (
                    <div className={`text-center py-8 rounded-lg backdrop-blur-sm ${
                      isNight ? 'bg-void-elevated/50 border border-cyber-cyan/10' : 'bg-gray-50/70 border border-gray-200/50'
                    }`}>
                      <p className={isNight ? 'text-gray-500' : 'text-gray-500'}>
                        {isNight ? 'No passkeys registered.' : 'No passkeys registered yet.'}
                      </p>
                      <p className={`text-sm mt-2 ${isNight ? 'text-gray-600' : 'text-gray-400'}`}>
                        {isNight ? 'Register a passkey for passwordless access.' : 'Register a passkey to enable passwordless login.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {passkeys.map((passkey) => (
                        <div
                          key={passkey.id}
                          className={`flex items-center justify-between p-4 rounded-lg backdrop-blur-sm ${
                            isNight ? 'bg-void-elevated/50 border border-cyber-cyan/10' : 'bg-gray-50/70 border border-gray-200/50'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isNight ? 'text-white' : 'text-gray-700'}`}>
                                🔑 Passkey
                              </span>
                              {passkey.transports && passkey.transports.length > 0 && (
                                <span className={`text-xs ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>
                                  ({passkey.transports.join(', ')})
                                </span>
                              )}
                            </div>
                            <p className={`text-xs mt-1 ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>
                              Registered: {new Date(passkey.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemovePasskey(passkey.id)}
                            className={`text-sm font-medium ${isNight ? 'text-cyber-magenta hover:text-red-400' : 'text-red-600 hover:text-red-800'}`}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info Section */}
                <div className={`mt-8 p-4 rounded-lg backdrop-blur-sm ${
                  isNight ? 'bg-cyber-cyan/10 border border-cyber-cyan/20' : 'bg-blue-50/70 border border-blue-200/50'
                }`}>
                  <h4 className={`text-sm font-semibold mb-2 ${isNight ? 'text-cyber-cyan' : 'text-blue-900'}`}>
                    {isNight ? 'ABOUT PASSKEYS' : 'What are Passkeys?'}
                  </h4>
                  <ul className={`text-sm space-y-1 ${isNight ? 'text-gray-400' : 'text-blue-800'}`}>
                    <li>• More secure than passwords - resistant to phishing</li>
                    <li>• Faster login - use your fingerprint or face</li>
                    <li>• No passwords to remember or type</li>
                    <li>• Works across your devices (when synced)</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerPortal
